import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { EASING } from '@animations/variants';

interface AuthGateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GATED_AREAS = ['Platform', 'Borrowers', 'Investors', 'Team'];

/**
 * The sign-in prompt raised whenever an unauthenticated visitor reaches gated
 * content. Dismissible — the content behind it stays blurred either way.
 */
export default function AuthGateModal({ isOpen, onClose }: AuthGateModalProps) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Close on Escape, and keep Tab inside the dialog while it's open.
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock background scroll and move focus into the dialog while it's open.
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    primaryRef.current?.focus();

    return () => {
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  function goToAuth(mode: 'signin' | 'signup') {
    onClose();
    // Return to the landing page after authenticating, not the portal —
    // the visitor was reading the site, not heading for the dashboard.
    navigate('/login', { state: { from: { pathname: '/' }, mode } });
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[150] flex items-center justify-center px-6 py-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASING }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[rgba(6,6,6,0.86)] backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-gate-title"
            aria-describedby="auth-gate-description"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: EASING }}
            className="relative w-full max-w-lg border border-border-card bg-bg-card/95 px-8 py-12 shadow-card backdrop-blur-nav sm:px-12"
          >
            {/* Ambient accent glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-accent-glow blur-[80px]"
            />

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-5 top-5 font-sans text-[0.625rem] uppercase tracking-nav text-text-faint transition-colors hover:text-text-primary"
            >
              Close
            </button>

            <div className="relative">
              <div className="mb-8 flex items-center gap-4">
                <span className="accent-line" aria-hidden />
                <span className="text-section-label">Restricted Access</span>
              </div>

              <h2
                id="auth-gate-title"
                className="mb-5 font-serif font-light text-text-primary"
                style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.1 }}
              >
                Sign in to continue <em className="not-italic text-accent">reading.</em>
              </h2>

              <p
                id="auth-gate-description"
                className="mb-8 font-serif text-text-secondary"
                style={{ fontSize: '1.0625rem', lineHeight: 1.75 }}
              >
                The rest of this page is reserved for registered users. Create an account or sign in
                to unlock the full platform overview.
              </p>

              <ul className="mb-10 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border-card pt-6">
                {GATED_AREAS.map((area) => (
                  <li
                    key={area}
                    className="flex items-center gap-3 font-sans text-label uppercase tracking-nav text-text-muted"
                  >
                    <span className="h-px w-4 shrink-0 bg-accent/50" aria-hidden />
                    {area}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  ref={primaryRef}
                  type="button"
                  onClick={() => goToAuth('signin')}
                  className="flex-1 bg-accent px-8 py-4 font-sans text-label font-medium uppercase tracking-nav text-bg-primary transition-opacity duration-200 hover:opacity-90"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => goToAuth('signup')}
                  className="flex-1 border border-accent/40 px-8 py-4 font-sans text-label uppercase tracking-nav text-text-primary transition-colors duration-200 hover:border-accent/70 hover:bg-accent/5"
                >
                  Create Account
                </button>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="mt-6 w-full text-center font-sans text-caption text-text-faint transition-colors hover:text-text-muted"
              >
                Keep browsing without an account
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
